import { create } from 'zustand';

export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  status: 'stopped' | 'starting' | 'running' | 'error';
  plan: string;
  maxPlayers: number;
  createdAt: string;
  port: number | null;
}

export interface SharedProjectSummary extends ProjectSummary {
  ownerUsername: string;
}

export interface ProjectInvite {
  projectId: string;
  projectName: string;
  ownerUsername: string;
  invitedAt: number;
}

export interface BlockedInvite {
  projectId: string;
  projectName: string;
  ownerUsername: string;
  blockedAt: number;
}

export interface Collaborator {
  userId: string;
  username: string;
  role: 'owner' | 'editor';
  pending?: boolean;
}

export interface TrashedProject {
  id: string;
  slug: string;
  name: string;
  deletedAt: number;
  purgeAt: number;
}

export interface RestorePoint {
  id: string;
  kind: 'daily' | 'before-restore' | 'before-import' | 'imported' | 'named';
  name: string | null;
  sizeBytes: number;
  createdAt: number;
}

export interface AccountUsage {
  plan: string;
  projects: { used: number; limit: number };
  storageMb: { used: number; limit: number; restorePoints: number };
  runningGames: { used: number; limit: number };
}

export const BUSY_MESSAGE = 'This game is busy making or restoring a copy. Try again in a moment.';

interface ProjectState {
  projects: ProjectSummary[];
  sharedProjects: SharedProjectSummary[];
  invites: ProjectInvite[];
  blockedInvites: BlockedInvite[];
  trash: TrashedProject[];
  usage: AccountUsage | null;
  isLoading: boolean;
  error: string | null;
  startErrors: Record<string, string>;

  fetchProjects: () => Promise<void>;
  createProject: (name: string, slug: string, templateId?: string) => Promise<ProjectSummary>;
  startProject: (id: string) => Promise<void>;
  stopProject: (id: string) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  fetchCollaborators: (id: string) => Promise<Collaborator[]>;
  addCollaborator: (id: string, username: string) => Promise<void>;
  removeCollaborator: (id: string, userId: string) => Promise<void>;
  acceptInvite: (id: string) => Promise<void>;
  declineInvite: (id: string, block: boolean) => Promise<void>;
  unblockInvites: (id: string) => Promise<void>;
  fetchTrash: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  untrashProject: (id: string) => Promise<void>;
  purgeProject: (id: string, confirmName: string) => Promise<void>;
  importProject: (file: Blob, name: string, slug: string) => Promise<ProjectSummary>;
  fetchRestorePoints: (id: string) => Promise<RestorePoint[]>;
  saveNamedVersion: (id: string, name: string) => Promise<void>;
  deleteRestorePoint: (id: string, pointId: string) => Promise<void>;
  restoreFromPoint: (id: string, pointId: string) => Promise<void>;
  replaceFromZip: (id: string, file: Blob) => Promise<void>;
  exportProject: (id: string) => Promise<void>;
}

async function errorFrom(res: Response, fallback: string): Promise<Error> {
  const data = await res.json().catch(() => ({}));
  if (data.code === 'project_busy') return new Error(BUSY_MESSAGE);
  return new Error(data.error || fallback);
}

function zipHeaders(): Record<string, string> {
  const token = localStorage.getItem('platform_token');
  return {
    'Content-Type': 'application/zip',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('platform_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const API = '/api/platform/projects';

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  sharedProjects: [],
  invites: [],
  blockedInvites: [],
  trash: [],
  usage: null,
  isLoading: false,
  error: null,
  startErrors: {},

  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(API, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = await res.json();
      set({
        projects: data.owned,
        sharedProjects: data.shared,
        invites: data.invites ?? [],
        blockedInvites: data.blockedInvites ?? [],
        isLoading: false,
      });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed' });
    }
  },

  createProject: async (name, slug, templateId) => {
    const res = await fetch(API, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name, slug, ...(templateId ? { templateId } : {}) }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create project');

    // Refresh list
    await get().fetchProjects();
    return data;
  },

  startProject: async (id) => {
    const res = await fetch(`${API}/${id}/start`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) {
      const data = await res.json();
      const message = data.error || 'Failed to start';
      set({ startErrors: { ...get().startErrors, [id]: message } });
      throw new Error(message);
    }
    const { [id]: _cleared, ...startErrors } = get().startErrors;
    set({ startErrors });
    await get().fetchProjects();
  },

  stopProject: async (id) => {
    const res = await fetch(`${API}/${id}/stop`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to stop');
    await get().fetchProjects();
  },

  deleteProject: async (id) => {
    const res = await fetch(`${API}/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to delete');
    set({ projects: get().projects.filter((p) => p.id !== id) });
  },

  fetchCollaborators: async (id) => {
    const res = await fetch(`${API}/${id}/collaborators`, { headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to load collaborators');
    return res.json();
  },

  addCollaborator: async (id, username) => {
    const res = await fetch(`${API}/${id}/collaborators`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username }),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to add collaborator');
  },

  removeCollaborator: async (id, userId) => {
    const res = await fetch(`${API}/${id}/collaborators/${userId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to remove collaborator');
    await get().fetchProjects();
  },

  acceptInvite: async (id) => {
    const res = await fetch(`${API}/${id}/invite/accept`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to accept invite');
    await get().fetchProjects();
  },

  declineInvite: async (id, block) => {
    const res = await fetch(`${API}/${id}/invite/decline`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ block }),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to decline invite');
    await get().fetchProjects();
  },

  unblockInvites: async (id) => {
    const res = await fetch(`${API}/${id}/invite/block`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to unblock invites');
    await get().fetchProjects();
  },

  fetchTrash: async () => {
    const res = await fetch(`${API}/trash`, { headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to load the trash');
    set({ trash: await res.json() });
  },

  fetchUsage: async () => {
    const res = await fetch(`${API}/usage`, { headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to load your usage');
    set({ usage: await res.json() });
  },

  untrashProject: async (id) => {
    const res = await fetch(`${API}/${id}/untrash`, { method: 'POST', headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to restore the game');
    set({ trash: get().trash.filter((p) => p.id !== id) });
    await get().fetchProjects();
  },

  purgeProject: async (id, confirmName) => {
    const res = await fetch(`${API}/${id}/purge`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ confirmName }),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to delete the game');
    set({ trash: get().trash.filter((p) => p.id !== id) });
  },

  importProject: async (file, name, slug) => {
    const query = new URLSearchParams({ name, slug });
    const res = await fetch(`${API}/import?${query}`, {
      method: 'POST',
      headers: zipHeaders(),
      body: file,
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to import the game');
    const data = await res.json();
    await get().fetchProjects();
    return data;
  },

  fetchRestorePoints: async (id) => {
    const res = await fetch(`${API}/${id}/restore-points`, { headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to load restore points');
    return res.json();
  },

  saveNamedVersion: async (id, name) => {
    const res = await fetch(`${API}/${id}/restore-points`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to save the version');
  },

  deleteRestorePoint: async (id, pointId) => {
    const res = await fetch(`${API}/${id}/restore-points/${pointId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to delete the version');
  },

  restoreFromPoint: async (id, pointId) => {
    const res = await fetch(`${API}/${id}/restore-points/${pointId}/restore`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to restore');
  },

  replaceFromZip: async (id, file) => {
    const res = await fetch(`${API}/${id}/replace`, {
      method: 'POST',
      headers: zipHeaders(),
      body: file,
    });
    if (!res.ok) throw await errorFrom(res, 'Failed to replace the game');
  },

  exportProject: async (id) => {
    const res = await fetch(`${API}/${id}/export`, { headers: getHeaders() });
    if (!res.ok) throw await errorFrom(res, 'Failed to download the game');
    const blob = await res.blob();
    const name =
      res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'game.zip';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
}));
